import type {
  MeteredPaywallStyles as DreamMeteredPaywallStyles,
  MeteredPaywallUsageBannerProps,
  Paywall,
  PostTheme,
} from '@shared/dream-components';

export type { Paywall };

export interface MeteredPaywallStyles {
  background_color?: string;
  text_color?: string;
  shadow?: string;
  button_background_color?: string;
  button_text_color?: string;
  input_background_color?: string;
  input_border_color?: string;
  placeholder_text_color?: string;
  input_text_color?: string;
  divider_color?: string;
  show_logo?: boolean;
  show_benefits?: boolean;
  logo_size?: number;
  back_button_color?: string;
}

interface MeteredPaywallCopy {
  title?: string;
  description?: string;
  email_placeholder_text?: string;
  subscribe_button_text?: string;
  upgrade_button_text?: string;
  benefits_header?: string;
  benefits?: string[];
  login_prompt?: string;
  login_link_text?: string;
}

export interface MeteredUsageStyles {
  background_color?: string;
  text_color?: string;
  button_background_color?: string;
  button_text_color?: string;
  close_icon_color?: string;
}

interface MeteredUsageCopy {
  title?: string;
  description?: string;
  button_text?: string;
}

export interface MeteredPaywall {
  id: number;
  paywall_type: 'metered';
  article_limit: number;
  interval_count: number;
  interval_unit: string;
  premium_offer_id?: string;
  logo_url?: string | null;
  show_tos_consent: boolean;
  paywall_styles: MeteredPaywallStyles;
  paywall_copy: MeteredPaywallCopy;
  usage_styles: MeteredUsageStyles;
  usage_copy: MeteredUsageCopy;
  theme?: PostTheme;
  created_at: string;
  updated_at: string;
}

export type AnyPaywall = Paywall | MeteredPaywall;

// Response shape from GET /paywalls/:id. The standard paywall lives at the top
// level (backwards-compatible with the prior shape and orchid-v1); when the
// publication has an active metered paywall, its config is nested under
// `metered_paywall`. The response is user-agnostic and safe to CDN-cache — the
// client picks which paywall to render using personalized
// `metered_paywall_limit_exceeded` from /post_meta_information.
export interface PaywallResponse extends Paywall {
  metered_paywall?: MeteredPaywall | null;
}

export function isMeteredPaywall(paywall: AnyPaywall): paywall is MeteredPaywall {
  return (paywall as MeteredPaywall).paywall_type === 'metered';
}

// Picks which paywall to render. When the viewer is a recognized subscriber who
// has exhausted the metered limit, fall back to the standard paywall — showing
// the metered email-capture modal again would re-ask their email and trip the
// /subscribe rate limit.
export function selectPaywall(
  response: PaywallResponse | null | undefined,
  meteredPaywallLimitExceeded: boolean | undefined,
): AnyPaywall | null {
  if (!response) return null;
  const { metered_paywall, ...standard } = response;
  if (metered_paywall && !meteredPaywallLimitExceeded) {
    return metered_paywall;
  }
  return standard as Paywall;
}

// Always returns the standard paywall, ignoring any metered configuration.
// For surfaces that never render the metered modal/banner (e.g. community
// post embeds, where those portal over the whole page).
export function selectStandardPaywall(response: PaywallResponse | null | undefined): Paywall | null {
  if (!response) return null;
  const { metered_paywall: _metered, ...standard } = response;
  return standard as Paywall;
}

export function toPaywallStyleProps(styles: MeteredPaywallStyles, logoUrl?: string | null): DreamMeteredPaywallStyles {
  return {
    backgroundColor: styles.background_color,
    textColor: styles.text_color,
    shadow: styles.shadow,
    buttonBackgroundColor: styles.button_background_color,
    buttonTextColor: styles.button_text_color,
    inputBackgroundColor: styles.input_background_color,
    inputBorderColor: styles.input_border_color,
    placeholderTextColor: styles.placeholder_text_color,
    inputTextColor: styles.input_text_color,
    dividerColor: styles.divider_color,
    showLogo: styles.show_logo,
    showBenefits: styles.show_benefits,
    logoSize: styles.logo_size,
    logoUrl: logoUrl ?? undefined,
    backButtonColor: styles.back_button_color,
  };
}

type UsageStyleProps = Pick<
  MeteredPaywallUsageBannerProps,
  'backgroundColor' | 'textColor' | 'buttonBackgroundColor' | 'buttonTextColor' | 'closeIconColor'
>;

export function toUsageStyleProps(styles: MeteredUsageStyles): UsageStyleProps {
  return {
    backgroundColor: styles.background_color,
    textColor: styles.text_color,
    buttonBackgroundColor: styles.button_background_color,
    buttonTextColor: styles.button_text_color,
    closeIconColor: styles.close_icon_color,
  };
}
